<script>
    import MaskInput from "svelte-input-mask/MaskInput.svelte";

    /**
     * Priority contact method switcher — the Svelte counterpart of
     * \Devsol\Main\Tools\ContactMethod (server side) and of
     * local/js/marchenkov/contact-method/script.js (PHP-rendered forms).
     * All three check the same rule and must phrase its error identically.
     *
     * The widget deliberately emits NONE of the data-contact-method* attributes:
     * script.js binds them by delegation on `document`, so a widget carrying them
     * would be driven by that global script and by Svelte at the same time.
     * Only the CSS classes are shared — their styling lives in the global styles.css.
     */

    /**
     * Mirror of ContactMethod::CHANNELS.
     *
     * MAX and PHONE point at the SAME field on purpose: MAX is registered by phone
     * number, so a separate MAX field would make the client type the same number
     * twice. The tab records only the preferred channel; the number stays shared,
     * which is exactly why it survives switching between those two tabs.
     */
    const CHANNELS = {
        MAX: {
            label: 'MAX',
            field: 'PHONE',
            errorName: 'MAX',
        },
        EMAIL: {
            label: 'Почта',
            field: 'EMAIL',
            errorName: 'почты',
        },
        PHONE: {
            label: 'Звонок',
            field: 'PHONE',
            errorName: 'звонка',
        },
    };

    // Wording must stay identical to ContactMethod::ERROR_* — the same rule is
    // checked on both sides and the client must not see it phrased two ways.
    const ERROR_NO_CHANNEL = 'Выберите способ связи';
    const ERROR_EMPTY_CONTACT = 'Заполните контакт для %s или выберите другой способ';

    /** Placeholder character of the phone mask — also the caret target on focus. */
    const MASK_CHAR = '_';

    /** Channel codes in display order — mirror of ContactMethod::channelsForForm(). */
    export let channels = ['MAX', 'EMAIL', 'PHONE'];

    /** Id prefix keeping the tabs unique when several forms share one page. */
    export let idPrefix = 'contact_method';

    /** Selected channel: the declared priority contact method. */
    export let channel = '';

    /** '+7' + unmasked digits — the shape the rest of the form stores a phone in. */
    export let phone = '';

    export let email = '';

    let error = '';
    let tabElements = [];

    $: availableChannels = channels.filter((code) => Boolean(CHANNELS[code]));

    // Mirrors ContactMethod::render(): a missing or unknown channel falls back to the
    // first tab, so the widget always opens with exactly one channel selected.
    $: if (!availableChannels.includes(channel)) {
        channel = availableChannels[0] ?? '';
    }

    $: activeField = CHANNELS[channel]?.field ?? '';

    // One pane per FIELD, not per tab: MAX and «Звонок» resolve to the same pane, so
    // the number is literally the same input and cannot be lost on a tab switch.
    $: paneFields = availableChannels
        .map((code) => CHANNELS[code].field)
        .filter((field, index, fields) => fields.indexOf(field) === index);

    /**
     * String value of a field, or null when no string can be taken from it.
     * Mirrors ContactMethod::scalarValue().
     */
    function scalarValue(value) {
        const single = Array.isArray(value) ? value[0] : value;

        if (single === null || single === undefined || typeof single === 'boolean') {
            return null;
        }

        return String(single);
    }

    /**
     * Phone normalization — behaviourally identical to ContactMethod::normalizePhone():
     * digits only, 10 digits that do not already start with 7 or 8 get a leading 7,
     * 11 digits starting with 8 become 7, anything but 11 digits means "not filled".
     *
     * The 11-digit rule is the point of the whole function: MaskInput runs with
     * alwaysShowMask, so an untouched or half-typed field yields undefined, '+7'
     * or '+79', and a plain empty check would let those through as a contact.
     */
    function normalizePhone(value) {
        const raw = scalarValue(value);
        if (raw === null) {
            return '';
        }

        let digits = raw.replace(/\D+/g, '');

        // The mask feeds in a '+7' prefix, so nine typed digits arrive here as exactly
        // 10 digits. Without this guard they would look like a national number and get
        // a second country code ('+7999999999' -> '77999999999'). A subscriber number
        // never starts with 7 or 8 after the country code (mobiles start with 9), so
        // 10 digits beginning with one of them are a truncated '+7...'/'8...', not a
        // national number missing its prefix.
        const isNationalNumberWithoutPrefix = digits.length === 10
            && !digits.startsWith('7')
            && !digits.startsWith('8');

        if (isNationalNumberWithoutPrefix) {
            digits = `7${digits}`;
        }

        if (digits.length === 11 && digits.startsWith('8')) {
            digits = `7${digits.slice(1)}`;
        }

        return digits.length === 11 ? digits : '';
    }

    /**
     * E-mail normalization — mirror of ContactMethod::normalizeEmail(): an "@" that is
     * not the first character and a dot inside the domain part sitting neither at its
     * start nor at its end.
     */
    function normalizeEmail(value) {
        const raw = scalarValue(value);
        if (raw === null) {
            return '';
        }

        const address = raw.trim();

        const atPosition = address.lastIndexOf('@');
        if (atPosition <= 0) {
            return '';
        }

        const domain = address.slice(atPosition + 1);
        const dotPosition = domain.indexOf('.');

        if (dotPosition <= 0 || dotPosition === domain.length - 1) {
            return '';
        }

        return address;
    }

    /**
     * Checks the rule "the active tab must be filled": the active tab IS the declared
     * priority channel, so it cannot stay empty; the other channel remains optional.
     *
     * @returns {boolean} true when the widget passes.
     */
    export function validate() {
        const config = CHANNELS[channel];

        if (!config || !availableChannels.includes(channel)) {
            error = ERROR_NO_CHANNEL;

            return false;
        }

        if (normalizeByField(config.field) !== '') {
            error = '';

            return true;
        }

        error = ERROR_EMPTY_CONTACT.replace('%s', config.errorName);

        return false;
    }

    /**
     * Entries for the submit payload. Codes are uppercase for BOTH forms on purpose:
     * submitAction() duplicates every property key in both cases before saving, so one
     * spelling serves the uppercase iblock (144) and the lowercase one (143) alike.
     *
     * A channel the client did not fill goes out as an empty string rather than as the
     * mask leftovers ('+7', '+79'), which have no business being stored as a contact.
     *
     * @returns {Array<{code: string, value: string}>}
     */
    export function getPayload() {
        return [
            {code: 'PHONE', value: normalizePhone(phone) !== '' ? phone : ''},
            {code: 'EMAIL', value: normalizeEmail(email)},
            {code: 'CONTACT_METHOD', value: channel},
        ];
    }

    function normalizeByField(field) {
        return field === 'EMAIL'
            ? normalizeEmail(email)
            : normalizePhone(phone);
    }

    function selectChannel(code) {
        channel = code;
        error = '';
    }

    function clearError() {
        error = '';
    }

    /**
     * Standard horizontal tablist keys; null means "not ours, leave the event alone".
     */
    function nextTabIndex(key, currentIndex, total) {
        switch (key) {
            case 'ArrowLeft':
                return (currentIndex - 1 + total) % total;
            case 'ArrowRight':
                return (currentIndex + 1) % total;
            case 'Home':
                return 0;
            case 'End':
                return total - 1;
            default:
                return null;
        }
    }

    function handleTabKeydown(event, index) {
        const nextIndex = nextTabIndex(
            event.key,
            index,
            availableChannels.length,
        );

        if (nextIndex === null) {
            return;
        }

        event.preventDefault();
        selectChannel(availableChannels[nextIndex]);
        tabElements[nextIndex]?.focus();
    }

    /**
     * MaskInput dispatches its own `change` on every keystroke; `unmaskedValue` holds
     * the ten digits typed after the static '+7' of the mask.
     */
    function handlePhoneChange({detail}) {
        phone = '+7' + detail.inputState.unmaskedValue;
        clearError();
    }

    /**
     * Puts the caret on the first free slot of the mask. That single rule covers every
     * case the field can be in: in an empty field the first placeholder is the first
     * editable position, in a partially filled one it is exactly the position after the
     * last typed digit (literal ')', ' ' and '-' are skipped for free), and a fully
     * filled number has no placeholder left, so the caret goes to the end.
     *
     * Deferred on purpose: on a MOUSE focus the browser applies the caret position of
     * the click AFTER `focus` has fired, so a synchronous setSelectionRange() here would
     * be silently overwritten. `focus` fires only when focus enters the field, so a
     * second click inside an already focused field still repositions the caret by hand.
     *
     * MaskInput dispatches focus with the input in `event.detail.target`.
     */
    function gotoFirstFreeSlot(event) {
        const input = event?.detail?.target;
        if (!input) {
            return;
        }

        setTimeout(
            () => {
                if (document.activeElement !== input) {
                    return;
                }

                const value = input.value ?? '';
                const slot = value.indexOf(MASK_CHAR);
                const position = slot === -1 ? value.length : slot;

                input.setSelectionRange(position, position);
            },
            0,
        );
    }
</script>

<div class="contact-method">
    <div class="contact-method__tabs" role="tablist">
        {#each availableChannels as code, index (code)}
            <button type="button"
                    class="contact-method__tab"
                    class:is-active={code === channel}
                    role="tab"
                    aria-selected={code === channel}
                    id="{idPrefix}_tab_{code.toLowerCase()}"
                    bind:this={tabElements[index]}
                    on:click={() => selectChannel(code)}
                    on:keydown={(event) => handleTabKeydown(event, index)}>{CHANNELS[code].label}</button>
        {/each}
    </div>

    {#if paneFields.includes('PHONE')}
        <div class="contact-method__pane"
             class:is-active={activeField === 'PHONE'}
             hidden={activeField !== 'PHONE'}>
            <MaskInput
                    name="PHONE"
                    class="phone"
                    type="tel"
                    aria-label="Телефон"
                    alwaysShowMask
                    mask="+7 (000) 000 - 00 - 00"
                    size={20}
                    showMask
                    maskChar={MASK_CHAR}
                    on:change={handlePhoneChange}
                    on:focus={gotoFirstFreeSlot}
            />
        </div>
    {/if}

    {#if paneFields.includes('EMAIL')}
        <div class="contact-method__pane"
             class:is-active={activeField === 'EMAIL'}
             hidden={activeField !== 'EMAIL'}>
            <input type="email"
                   id="{idPrefix}_email"
                   name="EMAIL"
                   aria-label="E-mail"
                   autocomplete="email"
                   bind:value={email}
                   on:input={clearError}/>
        </div>
    {/if}

    <span class="contact-method__error" hidden={!error}>{error}</span>
</div>
